import { api } from "./client";

export interface AdminUser {
  id: string;
  email: string;
  tier: string;
  stripeCustomerId: string | null;
  status: "active" | "banned";
  bannedAt: string | null;
  banReason: string | null;
  createdAt: string;
  activeKeyCount: number;
  requestsToday: number;
  requestsMonth: number;
}

export interface AdminUserBasic {
  id: string;
  email: string;
  tier: string;
  stripeCustomerId: string | null;
  status: "active" | "banned";
  bannedAt: string | null;
  banReason: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SecurityEvent {
  id: string;
  eventType: string;
  userId: string | null;
  actorUserId: string | null;
  apiKeyId: string | null;
  requestId: string | null;
  ip: string | null;
  userAgent: string | null;
  emailDomain: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
}

export interface RecentSourceIp {
  ip: string;
  firstSeenAt: string;
  lastSeenAt: string;
  requestCount: number;
}

export interface AdminApiKey {
  id: string;
  keyPrefix: string;
  name: string;
  tier: string;
  requestsToday: number;
  isActive: boolean;
  createdAt: string;
}

export interface MonthlyUsage {
  provider: string;
  model: string;
  tokensIn: number;
  tokensOut: number;
  requests: number;
  costCents: number;
}

export interface UsageByMonth {
  billingPeriod: string;
  provider: string;
  model: string;
  tokensIn: number;
  tokensOut: number;
  requests: number;
  costCents: number;
}

export interface AdminUserDetailResponse {
  user: AdminUserBasic;
  apiKeys: AdminApiKey[];
  securityEvents: SecurityEvent[];
  recentSourceIps: RecentSourceIp[];
  abuseFindings: AbuseFinding[];
  monthlyUsage: MonthlyUsage[];
  usageByMonth?: UsageByMonth[];
  totalCostCents?: number;
  range?: string;
  totalRequests: number;
}

export interface TierDistribution {
  free: number;
  supporter: number;
  pro: number;
  enterprise: number;
  total: number;
}

export interface TopUser {
  userId: string;
  email: string;
  tier: string;
  totalRequests: number;
}

export interface RequestLog {
  id: string;
  userId: string | null;
  email: string | null;
  method: string;
  path: string;
  statusCode: number | null;
  responseTimeMs: number | null;
  tier: string | null;
  createdAt: string;
}

export type AbuseWindow = "24h" | "7d" | "30d";
export type AbuseSignal =
  | "signup_velocity"
  | "linked_accounts"
  | "coordinated_quota_use"
  | "rapid_quota_exhaustion"
  | "relay_domain_velocity";
export type AbuseSeverity = "medium" | "high";
export type AbuseThresholdUnit = "accounts" | "users" | "percent";

export interface IpReputation {
  asn: string | null;
  organization: string | null;
  country: string | null;
  networkType: string | null;
}

export interface AbuseAffectedUser {
  userId: string;
  tier: string;
  requestCount?: number;
  quota?: number;
}

export interface AbuseFinding {
  signalType: AbuseSignal;
  severity: AbuseSeverity;
  threshold: { value: number; unit: AbuseThresholdUnit };
  firstSeenAt: string;
  lastSeenAt: string;
  affectedUsers: AbuseAffectedUser[];
  evidence: {
    ip?: string;
    userAgent?: string;
    emailDomain?: string;
    day?: string;
    eventCount?: number;
    requestCount?: number;
    quota?: number;
    elapsedMinutes?: number;
    accountCreatedAt?: string;
    perUser?: Array<{ userId: string; requestCount: number; quota: number }>;
  };
  ipReputation?: IpReputation;
}

export interface AbuseSummary {
  window: AbuseWindow;
  signal: AbuseSignal | null;
  total: number;
  highSeverity: number;
  mediumSeverity: number;
}

export interface AbuseFindingsResponse {
  summary: AbuseSummary;
  findings: AbuseFinding[];
  total: number;
  limit: number;
  offset: number;
}

export interface BanUserResponse {
  userId: string;
  bannedAt: string;
  banReason: string;
  revokedKeyCount: number;
}

export interface UnbanUserResponse {
  userId: string;
  active: boolean;
}

export interface LogStats {
  requestsToday: number;
  requestsThisHour: number;
  avgResponseTimeMs: number;
  errorRate: number;
  topEndpoints: Array<{ path: string; count: number }>;
}

export function getUsers(params: {
  search?: string;
  tier?: string;
  status?: "active" | "banned";
  limit?: number;
  offset?: number;
}) {
  const qs = new URLSearchParams();
  if (params.search) qs.set("search", params.search);
  if (params.tier) qs.set("tier", params.tier);
  if (params.status) qs.set("status", params.status);
  qs.set("limit", String(params.limit ?? 20));
  qs.set("offset", String(params.offset ?? 0));
  return api<{ users: AdminUser[]; total: number }>(`/admin/users?${qs}`);
}

export function getUser(id: string, range?: string) {
  const qs = range ? `?range=${range}` : "";
  return api<AdminUserDetailResponse>(`/admin/users/${id}${qs}`);
}

export function updateUserTier(id: string, tier: string) {
  return api<{ updated: boolean; tier: string }>(`/admin/users/${id}/tier`, {
    method: "PATCH",
    body: JSON.stringify({ tier }),
  });
}

export function getSubscriptions() {
  return api<TierDistribution>("/admin/subscriptions");
}

export function getTopUsage(limit = 10) {
  return api<{ topUsers: TopUser[] }>(`/admin/usage/top?limit=${limit}`);
}

export function getLogs(params: {
  userId?: string;
  path?: string;
  statusCode?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
}) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v != null) qs.set(k, String(v));
  });
  qs.set("limit", String(params.limit ?? 50));
  qs.set("offset", String(params.offset ?? 0));
  return api<{ logs: RequestLog[]; total: number }>(`/admin/logs?${qs}`);
}

export function getLogStats() {
  return api<LogStats>("/admin/logs/stats");
}

export function getAbuseFindings(params: {
  window?: AbuseWindow;
  signal?: AbuseSignal;
  limit?: number;
  offset?: number;
}) {
  const qs = new URLSearchParams();
  qs.set("window", params.window ?? "7d");
  if (params.signal) qs.set("signal", params.signal);
  qs.set("limit", String(params.limit ?? 20));
  qs.set("offset", String(params.offset ?? 0));
  return api<AbuseFindingsResponse>(`/admin/abuse?${qs}`);
}

export function banUser(id: string, reason: string) {
  return api<BanUserResponse>(`/admin/users/${encodeURIComponent(id)}/ban`, {
    method: "PATCH",
    body: JSON.stringify({ reason }),
  });
}

export function unbanUser(id: string) {
  return api<UnbanUserResponse>(`/admin/users/${encodeURIComponent(id)}/ban`, {
    method: "DELETE",
  });
}

// Wine Health
export interface WineHealthResponse {
  total: number;
  withEmbedding: number;
  needingEmbedding: number;
  withDescription: number;
  needingEnrichment: number;
  withScores: number;
  missingScores: number;
  withPrices: number;
  missingPrices: number;
  withPairings: number;
  missingPairings: number;
  enrichmentsLast7Days: number;
  pendingEnrichments: number;
}

export interface WineHealthHistoryEntry {
  date: string;
  total: number;
  failures: number;
  descriptions: number;
  scores: number;
  prices: number;
  pairings: number;
}

export interface WineHealthHistoryResponse {
  history: WineHealthHistoryEntry[];
}

export function getWineHealth() {
  return api<WineHealthResponse>("/admin/wine-health");
}

export function getWineHealthHistory() {
  return api<WineHealthHistoryResponse>("/admin/wine-health/history");
}

export interface EnterpriseOnboardResponse {
  sent: boolean;
  checkoutUrl: string;
  productId: string;
  priceId: string;
}

// Provider Status
export interface ProviderStatus {
  name: string;
  type: "llm" | "search";
  model: string | null;
  available: boolean;
  throttled: boolean;
  ttlSeconds: number | null;
  statusCode: number | null;
  reason: string | null;
  throttledAt: string | null;
}

export interface ProviderStatusResponse {
  providers: ProviderStatus[];
}

export function getProviderStatus() {
  return api<ProviderStatusResponse>("/admin/provider-status");
}

export function clearProviderStatus(name: string) {
  return api<{ cleared: boolean; provider: string }>(
    `/admin/provider-status/${encodeURIComponent(name)}`,
    { method: "DELETE" },
  );
}

export function throttleProvider(name: string) {
  return api<{ throttled: boolean; provider: string }>(
    `/admin/provider-status/${encodeURIComponent(name)}/throttle`,
    { method: "POST" },
  );
}


export function onboardEnterprise(params: {
  email: string;
  companyName: string;
  amountCents: number;
  interval: "month" | "year";
  dailyRateLimit?: number;
}) {
  return api<EnterpriseOnboardResponse>("/admin/enterprise/onboard", {
    method: "POST",
    body: JSON.stringify(params),
  });
}

// ── OpenCode models ──────────────────────────────────────────────

export interface OpenCodeModel {
  id: string;
  name: string;
  providerID: string;
  context: number;
}

export interface SelectedModel {
  providerID: string;
  modelID: string;
}

export function getOpenCodeModels() {
  return api<{ models: OpenCodeModel[]; selected: SelectedModel | null }>("/admin/opencode/models");
}

export function setOpenCodeModel(model: SelectedModel) {
  return api<{ updated: boolean; model: SelectedModel }>("/admin/opencode/model", {
    method: "PUT",
    body: JSON.stringify(model),
  });
}

// ── API Test Results ────────────────────────────────────────────

export interface TestResultThreshold {
  value: number;
  limit: number;
  passed: boolean;
}

export interface TestResultMetrics {
  requests: number;
  avgDuration: number;
  p95Duration: number;
  p99Duration: number;
  errorRate: number;
}

export interface TestResults {
  ranAt: string;
  overall: "pass" | "fail";
  contract: {
    status: string;
    exitCode: number;
    summary: string;
    failures: string[];
  };
  load: {
    status: string;
    exitCode: number;
    summary: string;
    thresholds: Record<string, TestResultThreshold>;
    metrics: TestResultMetrics;
  };
}

export function getTestResults() {
  return api<{ latest: TestResults | null; history: TestResults[] }>("/admin/test-results");
}
